import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, PageBody, fmtMoney } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash, CreditCard, Link as LinkIcon, EnvelopeSimple, Bank, FilePdf } from "@phosphor-icons/react";

const PAYSTACK_CURRENCIES = new Set(["NGN", "USD", "GHS", "ZAR", "KES"]);

export default function InvoicesPage() {
  const { activeCompany, formatApiError } = useAuth();
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [tab, setTab] = useState("AR");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm());

  function initialForm(type = "AR") {
    return {
      type,
      counterparty: "",
      invoice_number: "",
      date: new Date().toISOString().slice(0, 10),
      due_date: "",
      ar_ap_account_id: "",
      tax_amount: "",
      notes: "",
      lines: [{ description: "", quantity: 1, unit_price: "", account_id: "" }],
      files: [],
    };
  }

  const load = async () => {
    if (!activeCompany) return;
    const [i, a, s] = await Promise.all([
      api.get(`/companies/${activeCompany.id}/invoices?type=${tab}`),
      api.get(`/companies/${activeCompany.id}/accounts`),
      api.get(`/companies/${activeCompany.id}/salesmen`).catch(() => ({ data: [] })),
    ]);
    setRows(i.data);
    setAccounts(a.data);
    setSalesmen(s.data || []);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany?.id, tab]);

  const openNew = () => {
    setForm(initialForm(tab));
    setOpen(true);
  };

  const arAccounts = accounts.filter((a) =>
    tab === "AR" ? a.type === "asset" : a.type === "liability"
  );
  const lineAccounts = accounts.filter((a) =>
    tab === "AR" ? a.type === "revenue" : a.type === "expense"
  );

  const setLine = (idx, k, v) => {
    const lines = [...form.lines];
    lines[idx] = { ...lines[idx], [k]: v };
    setForm({ ...form, lines });
  };

  const total = form.lines.reduce(
    (s, l) => s + (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0),
    0
  ) + (parseFloat(form.tax_amount) || 0);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const attachment_ids = [];
      for (const f of form.files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post(`/companies/${activeCompany.id}/files`, fd);
        attachment_ids.push(data.id);
      }
      await api.post(`/companies/${activeCompany.id}/invoices`, {
        type: form.type,
        counterparty: form.counterparty,
        invoice_number: form.invoice_number,
        date: form.date,
        due_date: form.due_date || null,
        ar_ap_account_id: form.ar_ap_account_id,
        tax_amount: parseFloat(form.tax_amount) || 0,
        notes: form.notes,
        salesman_id: form.type === "AR" && form.salesman_id ? form.salesman_id : null,
        lines: form.lines
          .filter((l) => l.account_id && l.unit_price)
          .map((l) => ({
            description: l.description,
            quantity: parseFloat(l.quantity) || 1,
            unit_price: parseFloat(l.unit_price) || 0,
            account_id: l.account_id,
          })),
        attachment_ids,
      });
      toast.success(`${tab === "AR" ? "Invoice" : "Bill"} created`);
      setOpen(false);
      load();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/companies/${activeCompany.id}/invoices/${id}`);
    toast.success("Deleted");
    load();
  };

  const payWithStripe = async (id) => {
    try {
      const { data } = await api.post(
        `/companies/${activeCompany.id}/invoices/${id}/checkout`,
        { origin_url: window.location.origin }
      );
      window.location.href = data.checkout_url;
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  const payWithPaystack = async (row) => {
    const email = window.prompt(
      `Customer email for Paystack receipt:`,
      `customer@example.com`
    );
    if (!email) return;
    try {
      const { data } = await api.post(
        `/companies/${activeCompany.id}/invoices/${row.id}/paystack/init`,
        { origin_url: window.location.origin, email }
      );
      window.location.href = data.authorization_url;
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  const getPayLink = async (id) => {
    const { data } = await api.get(`/companies/${activeCompany.id}/invoices/${id}/pay-link`);
    return `${window.location.origin}/pay/${data.pay_token}`;
  };

  const copyPayLink = async (id) => {
    try {
      const url = await getPayLink(id);
      await navigator.clipboard.writeText(url);
      toast.success("Pay link copied to clipboard");
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  const emailPayLink = async (inv) => {
    // One-click email via Resend (PDF attached, HTML body, pay-link button)
    const defaultTo = inv.customer_email || inv.counterparty_email || "";
    const to = window.prompt("Send invoice to (email):", defaultTo);
    if (!to) return;
    try {
      const { data } = await api.post(
        `/companies/${activeCompany.id}/invoices/${inv.id}/email`,
        { to }
      );
      toast.success(`Emailed to ${data.to}`);
    } catch (e2) {
      const detail = formatApiError(e2.response?.data?.detail) || e2.message;
      if (e2.response?.status === 503) {
        toast.error("Email not configured. Set RESEND_API_KEY in the backend .env.");
      } else {
        toast.error(detail);
      }
    }
  };

  if (!activeCompany) return <PageBody><div className="overline">Select a company</div></PageBody>;

  return (
    <>
      <PageHeader
        eyebrow="Sub-Ledger"
        title="Invoices & Bills"
        description="AR invoices track receivables; AP bills track payables. Every document auto-posts a journal."
        actions={
          <Button
            onClick={openNew}
            data-testid="new-invoice-btn"
            className="rounded-sm h-9 font-mono uppercase tracking-wider text-xs"
          >
            <Plus size={14} className="mr-2" /> New {tab === "AR" ? "Invoice" : "Bill"}
          </Button>
        }
      />
      <PageBody>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-sm bg-muted">
            <TabsTrigger value="AR" data-testid="tab-ar" className="rounded-sm font-mono uppercase text-xs">
              AR · Sales Invoices
            </TabsTrigger>
            <TabsTrigger value="AP" data-testid="tab-ap" className="rounded-sm font-mono uppercase text-xs">
              AP · Bills
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/40">
              <tr className="text-left">
                <Th>Date</Th>
                <Th>#</Th>
                <Th>Counterparty</Th>
                <Th>Due</Th>
                <Th className="text-right">Subtotal</Th>
                <Th className="text-right">Tax</Th>
                <Th className="text-right">Total</Th>
                <Th className="text-right pr-4"></Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`invoice-row-${r.id}`} className="border-b border-border last:border-b-0 hover:bg-muted/40 transition-colors duration-200">
                  <Td className="num">{r.date}</Td>
                  <Td className="num">{r.invoice_number || "—"}</Td>
                  <Td>{r.counterparty}</Td>
                  <Td className="num">{r.due_date || "—"}</Td>
                  <Td className="text-right num">{fmtMoney(r.subtotal, activeCompany.currency)}</Td>
                  <Td className="text-right num">{fmtMoney(r.tax_amount, activeCompany.currency)}</Td>
                  <Td className="text-right num font-medium">
                    {fmtMoney(r.total, activeCompany.currency)}
                    {r.payment_status === "paid" && (
                      <span className="ml-2 tag-badge text-primary">PAID</span>
                    )}
                  </Td>
                  <Td className="text-right pr-4">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.open(
                          `${process.env.REACT_APP_BACKEND_URL}/api/companies/${activeCompany.id}/invoices/${r.id}/pdf`,
                          "_blank"
                        )}
                        data-testid={`invoice-pdf-${r.id}`}
                        title="Download PDF"
                        className="rounded-sm h-8"
                      >
                        <FilePdf size={12} />
                      </Button>
                      {tab === "AR" && r.payment_status !== "paid" && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyPayLink(r.id)}
                            data-testid={`invoice-copy-link-${r.id}`}
                            title="Copy pay link"
                            className="rounded-sm h-8 font-mono text-xs uppercase tracking-wider"
                          >
                            <LinkIcon size={12} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => emailPayLink(r)}
                            data-testid={`invoice-email-${r.id}`}
                            title="Email invoice"
                            className="rounded-sm h-8 font-mono text-xs uppercase tracking-wider"
                          >
                            <EnvelopeSimple size={12} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => payWithStripe(r.id)}
                            data-testid={`invoice-pay-${r.id}`}
                            title="Pay with Stripe"
                            className="rounded-sm h-8 font-mono text-xs uppercase tracking-wider"
                          >
                            <CreditCard size={12} className="mr-1" /> Stripe
                          </Button>
                          {PAYSTACK_CURRENCIES.has(activeCompany.currency) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => payWithPaystack(r)}
                              data-testid={`invoice-paystack-${r.id}`}
                              title="Pay with Paystack"
                              className="rounded-sm h-8 font-mono text-xs uppercase tracking-wider"
                            >
                              <Bank size={12} className="mr-1" /> Paystack
                            </Button>
                          )}
                        </>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => remove(r.id)} className="rounded-sm h-8 text-destructive">
                        <Trash size={14} />
                      </Button>
                    </div>
                  </Td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-muted-foreground">
                    No {tab === "AR" ? "invoices" : "bills"} yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif-display text-2xl">
                New {tab === "AR" ? "Sales Invoice" : "Bill"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest">
                    {tab === "AR" ? "Customer" : "Vendor"}
                  </Label>
                  <Input
                    data-testid="invoice-counterparty"
                    value={form.counterparty}
                    onChange={(e) => setForm({ ...form, counterparty: e.target.value })}
                    required
                    className="rounded-sm font-mono"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Number</Label>
                  <Input
                    data-testid="invoice-number"
                    value={form.invoice_number}
                    onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                    className="rounded-sm font-mono"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Date</Label>
                  <Input
                    type="date"
                    data-testid="invoice-date"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    required
                    className="rounded-sm font-mono"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Due Date</Label>
                  <Input
                    type="date"
                    value={form.due_date}
                    onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                    className="rounded-sm font-mono"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs uppercase tracking-widest">
                    {tab === "AR" ? "AR Account" : "AP Account"}
                  </Label>
                  <Select
                    value={form.ar_ap_account_id}
                    onValueChange={(v) => setForm({ ...form, ar_ap_account_id: v })}
                  >
                    <SelectTrigger data-testid="invoice-ar-ap" className="rounded-sm font-mono">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {arAccounts.map((a) => (
                        <SelectItem key={a.id} value={a.id} className="font-mono">
                          {a.code} · {a.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {tab === "AR" && (
                  <div className="col-span-1">
                    <Label className="text-xs uppercase tracking-widest">Salesman (optional)</Label>
                    <Select
                      value={form.salesman_id || "__none__"}
                      onValueChange={(v) => setForm({ ...form, salesman_id: v === "__none__" ? "" : v })}
                    >
                      <SelectTrigger data-testid="invoice-salesman" className="rounded-sm font-mono">
                        <SelectValue placeholder="None" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__" className="font-mono text-muted-foreground">
                          — None —
                        </SelectItem>
                        {salesmen.map((s) => (
                          <SelectItem key={s.id} value={s.id} className="font-mono">
                            {s.name} <span className="text-xs text-muted-foreground ml-1">({s.commission_pct}%)</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div className="border border-border">
                <div className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-border bg-muted/40 overline">
                  <div className="col-span-4">Description</div>
                  <div className="col-span-4">{tab === "AR" ? "Revenue Account" : "Expense Account"}</div>
                  <div className="col-span-1 text-right">Qty</div>
                  <div className="col-span-2 text-right">Price</div>
                  <div className="col-span-1" />
                </div>
                {form.lines.map((l, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 px-3 py-2 border-b border-border last:border-b-0">
                    <Input
                      value={l.description}
                      onChange={(e) => setLine(idx, "description", e.target.value)}
                      className="col-span-4 rounded-sm font-mono"
                    />
                    <div className="col-span-4">
                      <Select value={l.account_id} onValueChange={(v) => setLine(idx, "account_id", v)}>
                        <SelectTrigger className="rounded-sm font-mono h-9">
                          <SelectValue placeholder="Account" />
                        </SelectTrigger>
                        <SelectContent>
                          {lineAccounts.map((a) => (
                            <SelectItem key={a.id} value={a.id} className="font-mono">
                              {a.code} · {a.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input
                      type="number"
                      step="0.01"
                      value={l.quantity}
                      onChange={(e) => setLine(idx, "quantity", e.target.value)}
                      className="col-span-1 rounded-sm font-mono text-right"
                    />
                    <Input
                      type="number"
                      step="0.01"
                      value={l.unit_price}
                      onChange={(e) => setLine(idx, "unit_price", e.target.value)}
                      className="col-span-2 rounded-sm font-mono text-right"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setForm({ ...form, lines: form.lines.filter((_, i) => i !== idx) })}
                      className="col-span-1 rounded-sm h-9"
                    >
                      <Trash size={14} />
                    </Button>
                  </div>
                ))}
                <div className="px-3 py-2 bg-muted/60 flex items-center justify-between">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setForm({
                        ...form,
                        lines: [...form.lines, { description: "", quantity: 1, unit_price: "", account_id: "" }],
                      })
                    }
                    className="rounded-sm font-mono text-xs h-8"
                  >
                    <Plus size={12} className="mr-1" /> Line
                  </Button>
                  <div className="font-mono text-sm">
                    Total: <span className="num font-medium">{total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest">Tax Amount</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.tax_amount}
                    onChange={(e) => setForm({ ...form, tax_amount: e.target.value })}
                    className="rounded-sm font-mono text-right"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Attachments</Label>
                  <Input
                    type="file"
                    multiple
                    onChange={(e) => setForm({ ...form, files: Array.from(e.target.files || []) })}
                    className="rounded-sm font-mono"
                  />
                </div>
              </div>

              <DialogFooter>
                <Button
                  type="submit"
                  data-testid="invoice-submit"
                  className="rounded-sm font-mono uppercase tracking-wider text-xs"
                >
                  Post {tab === "AR" ? "Invoice" : "Bill"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </PageBody>
    </>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 overline ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 ${className}`}>{children}</td>
);
